import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram } from 'lucide-react';
import { Separator } from '../ui/separator';
import { getSiteSettings } from '../../lib/api';
import Telemetry from './Telemetry';

const PLATFORM_ICONS = {
  Instagram: Instagram,
};

export default function Footer() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    getSiteSettings()
      .then(res => setSettings(res.data))
      .catch(() => {});
  }, []);

  const contact = settings?.contact || { email: 'info@visualte.com', phone: '+972 50 237 2931' };
  const ecosystem = settings?.ecosystem || [
    { name: 'Onlenu', url: '/powered/onlenu' },
    { name: 'ELT Studios', url: '/powered/elt-studios' },
  ];
  const social = settings?.social || [{ platform: 'Instagram', url: 'https://instagram.com/visualte.co', handle: '@visualte.co' }];

  return (
    <footer className="border-t border-white/8 bg-[#070A0E] relative" data-testid="footer">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img
                src="/assets/logos/visualte-logo.png"
                alt="Visualte"
                className="h-7 w-7 object-contain"
              />
              <span className="text-base font-semibold tracking-[-0.02em]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                VISUALTE
              </span>
            </Link>
            <p className="text-sm text-white/55 leading-relaxed">
              The power engine behind scalable digital brands. Infrastructure that drives growth.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-xs font-medium tracking-[0.22em] uppercase text-white/55 mb-4">Navigation</h4>
            <div className="flex flex-col gap-2.5">
              <Link to="/" className="text-sm text-white/70 hover:text-white transition-colors">Home</Link>
              <Link to="/about" className="text-sm text-white/70 hover:text-white transition-colors">About</Link>
              <Link to="/ecosystem" className="text-sm text-white/70 hover:text-white transition-colors">Ecosystem</Link>
              <Link to="/journal" className="text-sm text-white/70 hover:text-white transition-colors">Journal</Link>
              <Link to="/contact" className="text-sm text-white/70 hover:text-white transition-colors">Contact</Link>
            </div>
          </div>

          {/* Ecosystem */}
          <div>
            <h4 className="text-xs font-medium tracking-[0.22em] uppercase text-white/55 mb-4">Ecosystem</h4>
            <div className="flex flex-col gap-2.5">
              {ecosystem.map((item, i) => (
                <Link key={i} to={item.url} className="text-sm text-white/70 hover:text-white transition-colors" data-testid={`footer-ecosystem-${i}`}>
                  {item.name}
                </Link>
              ))}
              {ecosystem.length === 0 && <span className="text-sm text-white/30">Coming soon</span>}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-medium tracking-[0.22em] uppercase text-white/55 mb-4">Contact</h4>
            <div className="flex flex-col gap-2.5">
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="text-sm text-white/70 hover:text-[#E8722A] transition-colors" data-testid="footer-contact-email">
                  {contact.email}
                </a>
              )}
              {contact.phone && (
                <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="text-sm text-white/70 hover:text-[#E8722A] transition-colors" data-testid="footer-contact-phone">
                  {contact.phone}
                </a>
              )}
            </div>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-xs font-medium tracking-[0.22em] uppercase text-white/55 mb-4">Follow us on:</h4>
            <div className="flex flex-col gap-2.5">
              {social.map((item, i) => {
                const Icon = PLATFORM_ICONS[item.platform];
                return (
                  <a
                    key={i}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-white/70 hover:text-[#E8722A] transition-colors group"
                    data-testid={`footer-social-${item.platform.toLowerCase()}`}
                  >
                    {Icon && <Icon className="h-4 w-4 group-hover:text-[#E8722A] transition-colors" />}
                    <span>{item.platform}</span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <Separator className="my-10 bg-white/8" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            &copy; {new Date().getFullYear()} Visualte. All rights reserved.
          </p>
          <Telemetry items={[{ k: 'ENGINE', v: 'DIGITAL INFRASTRUCTURE' }]} data-testid="footer-telemetry" />
        </div>
      </div>
    </footer>
  );
}
